import { feedEndpointLabel } from '@/lib/infra/feedEndpointLabel';
import { useEffect, useState } from 'react';
import { getFeedTransports, type FeedTransportSnapshot } from '@/hooks/useHydra';
import { formatCharsPerSec } from '@/lib/telemetry/reportContext';

// Live feed transport — CLIENT-SIDE (this browser/tab's hydra WS), polled from
// the useHydra singleton. The headline diagnostic for the "every reload/window
// forms a new connection" report: SharedWorker = one upstream shared across all
// this browser's tabs; direct = a separate WS per window (the fallback where
// `type:'module'` SharedWorkers aren't supported, e.g. Safari). Reflects only the
// browser you're viewing this in — load it where the symptom is seen.
//
// Part 0 of g1xl-v2#1053 (the Polymarket ladder day): per feed, what this
// MACHINE pulls — frames/s and decoded JSON chars/s on the one upstream socket (the
// worker's tally, shared by every tab of this browser), the rows the store
// holds from that feed per raw sport key, and the filter the connection
// actually negotiated (the engine's ack echo, with its generation). The Live
// Feed panel shows per-tab state; this is the per-connection view.
function transportBadge(t: 'shared' | 'direct' | 'connecting') {
  if (t === 'shared') return { cls: 'bg-green-600/80 text-white', label: 'SharedWorker' };
  if (t === 'direct') return { cls: 'bg-amber-500/80 text-white', label: 'direct WS' };
  return { cls: 'bg-muted text-muted-foreground', label: 'connecting' };
}

/** The negotiated filter in one line: `CFB,CFBALT · 1 game · 250→250ms · g7`.
 *  Absent axes read as their wire meaning (every sport / every game). */
export function fmtFilter(f: NonNullable<FeedTransportSnapshot['filter']>): string {
  if (f.scopeKnown === false) return `scope unavailable · g${f.generation}${f.error ? ` · REJECTED ${f.error}` : ''}`;
  const parts: string[] = [];
  parts.push(f.tickers != null ? `${f.tickers} tickers` : f.sports && f.sports.length > 0 ? f.sports.join(',') : 'all sports');
  if (f.tickers == null) parts.push(f.games != null ? `${f.games} game${f.games === 1 ? '' : 's'}` : 'all games');
  parts.push(f.effectiveCadenceMs && f.effectiveCadenceMs !== f.cadenceMs ? `${f.cadenceMs}→${f.effectiveCadenceMs}ms` : `${f.cadenceMs}ms`);
  parts.push(`g${f.generation}`);
  if (f.error) parts.push(`REJECTED ${f.error}`);
  return parts.join(' · ');
}

/** Rows held, biggest buckets first: `CFB 14,850 · CFBALT 2,100`. */
export function fmtRows(rows: FeedTransportSnapshot['rows']): string {
  if (!rows || rows.length === 0) return '—';
  return rows.map((r) => `${r.sport.toUpperCase()} ${r.rows.toLocaleString()}`).join(' · ');
}

export function FeedTransport() {
  const [feeds, setFeeds] = useState<FeedTransportSnapshot[]>(getFeedTransports);
  useEffect(() => {
    const id = setInterval(() => setFeeds(getFeedTransports()), 2000);
    return () => clearInterval(id);
  }, []);
  const anyDirect = feeds.some((f) => f.transport === 'direct');
  const allShared = feeds.length > 0 && feeds.every((f) => f.transport === 'shared');
  const totalBytes = feeds.reduce((sum, f) => sum + (f.pull?.bytesPerSec ?? 0), 0);
  const totalRows = feeds.reduce((sum, f) => sum + (f.rows ?? []).reduce((s, r) => s + r.rows, 0), 0);
  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Live feed transport</h2>
        {feeds.length > 0 && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
              anyDirect ? 'bg-amber-500/80 text-white' : allShared ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
            }`}
          >
            {anyDirect ? 'DIRECT — one WS per window' : allShared ? 'SHARED — one WS across tabs' : 'CONNECTING'}
          </span>
        )}
        {feeds.length > 0 && (
          <span className="text-foreground/80 font-mono text-[11px]" data-testid="feed-transport-totals">
            {formatCharsPerSec(totalBytes)} JSON chars/s in · {totalRows.toLocaleString()} rows held
          </span>
        )}
        <span className="text-muted-foreground text-[11px]">
          this browser&apos;s hydra feed · SharedWorker shares one upstream across all your tabs; a direct fallback
          (Safari / unsupported) opens a separate socket per window — the &ldquo;new connection every reload/window&rdquo; symptom.
          chars/s = JSON characters the machine receives and decodes on that one socket (every tab shares it), not network bytes; rows = what this tab&apos;s store holds
          from the feed; filter = the engine&apos;s ack, not the request.
        </span>
      </div>
      {feeds.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">
          No feed client on this tab yet — still connecting, or the desk shell isn&apos;t mounted here.
        </p>
      ) : (
        <table className="w-full text-left text-xs font-mono">
          <thead className="text-muted-foreground border-border border-b">
            <tr>
              <th className="py-1.5 pr-3 font-medium">Feed</th>
              <th className="px-3 font-medium">Role</th>
              <th className="px-3 font-medium">Transport</th>
              <th className="px-3 text-center font-medium" title="upstream WS connected">Live</th>
              <th className="px-3 text-right font-medium" title="frames per second on the upstream socket, last tally window">frames/s</th>
              <th className="px-3 text-right font-medium" title="decoded JSON characters per second on the upstream socket (raw.length, before compression), last tally window; not network bytes">chars/s</th>
              <th className="px-3 text-right font-medium" title="tabs of this browser sharing the socket">tabs</th>
              <th className="px-3 font-medium" title="rows this tab's store holds from the feed, per raw sport key">rows held</th>
              <th className="px-3 font-medium" title="the negotiated markets filter: the engine's ack echo and its generation">filter</th>
              <th className="px-3 font-medium">Upstream</th>
            </tr>
          </thead>
          <tbody>
            {feeds.map((f) => {
              const t = transportBadge(f.transport);
              const pull = f.pull ?? null;
              const stale = pull != null && pull.ageMs > 15_000;
              return (
                <tr key={f.id} className="border-border/50 border-b" data-testid={`feed-transport-row-${f.id}`}>
                  <td className="text-foreground py-1.5 pr-3">{f.label ? `${f.label} (${f.id})` : f.id}</td>
                  <td className="text-muted-foreground px-3">{f.role}</td>
                  <td className="px-3">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${t.cls}`}>{t.label}</span>
                  </td>
                  <td className="px-3 text-center">
                    {f.connected ? <span className="text-green-400">●</span> : <span className="text-muted-foreground">○</span>}
                  </td>
                  <td className={`px-3 text-right tabular-nums ${stale ? 'text-muted-foreground' : 'text-foreground'}`} title={pull ? `${pull.ageMs} ms old` : 'no tally yet'}>
                    {pull ? pull.framesPerSec.toFixed(1) : '—'}
                  </td>
                  <td className={`px-3 text-right tabular-nums ${pull && pull.bytesPerSec >= 1_000_000 ? 'text-amber-300 light:text-amber-700' : stale ? 'text-muted-foreground' : 'text-foreground'}`} title={pull ? `${pull.ageMs} ms old` : 'no tally yet'}>
                    {pull ? formatCharsPerSec(pull.bytesPerSec) : '—'}
                  </td>
                  <td className="text-muted-foreground px-3 text-right tabular-nums">{pull ? pull.tabs : '—'}</td>
                  <td className="text-foreground/80 px-3">{fmtRows(f.rows)}</td>
                  <td className={`px-3 ${f.filter?.error ? 'text-red-400' : 'text-foreground/80'}`}>
                    {f.filter ? fmtFilter(f.filter) : <span className="text-muted-foreground">legacy / no ack</span>}
                  </td>
                  <td className="text-muted-foreground max-w-[220px] truncate px-3" title={feedEndpointLabel(f.url)}>
                    {feedEndpointLabel(f.url)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
